import React from 'react';
import InputField from '../../../../../components/CredentialsInput/InputField';
import { kgToImperial, stLbToKg, WeightUnit } from '../../../../../utils/weightUnits';

interface WeightInputProps {
	id: string;
	language: string;
	unit: WeightUnit;
	/** Shows the kg / st-lb switch (UK only). */
	allowUnitChange: boolean;
	onUnitChange: (unit: WeightUnit) => void;
	/** Value in kg, or undefined while empty. */
	valueKg: number | undefined;
	onChangeKg: (kg: number | undefined) => void;
	onBlur?: () => void;
	kgPlaceholder: string;
	errorMsg: string;
}

const digitsOnly = (value: string) => value.replace(/\D/g, '');

const UnitSwitch: React.FC<{ unit: WeightUnit; onChange: (unit: WeightUnit) => void }> = ({ unit, onChange }) => (
	<div className="flex self-end rounded-full bg-[#f2eee8] p-0.5 text-xs font-medium" role="group" aria-label="Weight unit">
		{(['st', 'kg'] as WeightUnit[]).map((option) => (
			<button
				key={option}
				type="button"
				onClick={() => onChange(option)}
				aria-pressed={unit === option}
				className={`rounded-full border-none px-3 py-1 cursor-pointer ${unit === option ? 'bg-white text-care-plus-heading shadow-sm' : 'bg-transparent text-[#616975]'}`}
			>
				{option === 'st' ? 'st / lb' : 'kg'}
			</button>
		))}
	</div>
);

/** Weight entry in kg or in stones + pounds; always reports kg (one decimal) to the form. */
const WeightInput: React.FC<WeightInputProps> = ({
	id, language, unit, allowUnitChange, onUnitChange, valueKg, onChangeKg, onBlur, kgPlaceholder, errorMsg,
}) => {
	const [rawKg, setRawKg] = React.useState(valueKg != null ? String(valueKg) : '');
	const initialImperial = valueKg != null ? kgToImperial(valueKg) : null;
	const [rawSt, setRawSt] = React.useState(initialImperial ? String(initialImperial.st) : '');
	const [rawLb, setRawLb] = React.useState(initialImperial ? String(initialImperial.lb) : '');

	// Switching unit re-expresses whatever was entered in the new unit.
	React.useEffect(() => {
		if (valueKg == null) return;
		if (unit === 'st') {
			const { st, lb } = kgToImperial(valueKg);
			setRawSt(String(st));
			setRawLb(String(lb));
		} else {
			setRawKg(String(valueKg));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [unit]);

	const emitImperial = (st: string, lb: string) => {
		if (st === '' && lb === '') {
			onChangeKg(undefined);
			return;
		}
		// No cap on lb: what is typed is what is saved (14+ lb simply rolls into stones).
		onChangeKg(stLbToKg(Number(st) || 0, Number(lb) || 0));
	};

	return (
		<div className="flex flex-col gap-y-2">
			{allowUnitChange && <UnitSwitch unit={unit} onChange={onUnitChange} />}
			{unit === 'st' ? (
				<div className="flex gap-x-3">
					<InputField
						id={`${id}-st`}
						type="text"
						mandatory={true}
						placeholder="st"
						language={language}
						value={rawSt}
						onChange={(value: string) => {
							const next = digitsOnly(value).slice(0, 2);
							setRawSt(next);
							emitImperial(next, rawLb);
						}}
						onBlur={onBlur}
						touched={true}
						ignoreTouched={true}
						errorMsg={errorMsg}
					/>
					<InputField
						id={`${id}-lb`}
						type="text"
						mandatory={false}
						placeholder="lb"
						language={language}
						value={rawLb}
						onChange={(value: string) => {
							const next = digitsOnly(value).slice(0, 2);
							setRawLb(next);
							emitImperial(rawSt, next);
						}}
						onBlur={onBlur}
						touched={true}
						ignoreTouched={true}
						errorMsg=""
					/>
				</div>
			) : (
				<InputField
					id={id}
					type="text"
					mandatory={true}
					placeholder={kgPlaceholder}
					language={language}
					value={rawKg}
					onChange={(value: string) => {
						const next = digitsOnly(value);
						setRawKg(next);
						onChangeKg(next === '' ? undefined : Number(next));
					}}
					onBlur={onBlur}
					touched={true}
					ignoreTouched={true}
					errorMsg={errorMsg}
				/>
			)}
		</div>
	);
};

export default WeightInput;
